import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import { CommonTableCellProps } from "./types";

import styles from "./TableCells.module.scss";

export type SimpleTableBodyCellProps = ComponentPropsWithoutRef<"td"> &
  CommonTableCellProps;

export type DataTableBodyCellProps = ComponentPropsWithoutRef<"div"> &
  CommonTableCellProps;

/**
 * The props for the TableBodyCell component
 */
export type TableBodyCellProps =
  | ({
      /**
       * The type of table being rendered
       */
      type: "simple";
    } & SimpleTableBodyCellProps)
  | ({
      /**
       * The type of table being rendered
       */
      type: "data-table";
    } & DataTableBodyCellProps);

/**
 * The styled table body cell component
 * @param props - The props for the TableBodyCell component
 * @param props.type - The type of table being rendered
 * @param props.children - The children to render in the cell
 * @param props.className - The class name of the TableBodyCell component
 * @param props.rest - The rest of the props
 */
export const TableBodyCell = forwardRef<
  HTMLTableCellElement | HTMLDivElement,
  TableBodyCellProps
>(({ type, children, className, isExpandCell, isSelectCell, ...rest }, ref) => {
  const classes = cx(styles["table-body-cell"], className, {
    [styles["table-cell-internal-expand"]]: isExpandCell,
    [styles["table-cell-internal-select"]]: isSelectCell,
  });
  const CellElement = type === "simple" ? "td" : "div";

  return (
    <CellElement
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- could be table cell or div
      ref={ref as any}
      data-anv="table-body-cell"
      {...rest}
      className={classes}
      role={type === "simple" ? undefined : "gridcell"}
    >
      {children}
    </CellElement>
  );
});

TableBodyCell.displayName = "TableBodyCell";
